import { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, ChevronRight, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { truncateName } from '@/lib/nameUtils';
import { useIsMobile } from '@/hooks/use-mobile';
interface PlayerInfo {
  id: string;
  name: string;
  avatar: string | null;
  slug: string;
}

interface TodayMatch {
  id: string;
  team_a_name: string | null;
  team_b_name: string | null;
  score_a: number | null;
  score_b: number | null;
  match_date: string | null;
  status: string;
  player_a: PlayerInfo | null;
  player_b: PlayerInfo | null;
}

interface TodayMatchesTickerProps {
  matches: TodayMatch[];
  isLoading: boolean;
}

const MatchItem = ({ match, compact = false, onClick }: { match: TodayMatch; compact?: boolean; onClick?: () => void }) => {
  const playerAName = match.player_a?.name || match.team_a_name || 'TBD';
  const playerBName = match.player_b?.name || match.team_b_name || 'TBD';
  
  return (
    <div 
      onClick={onClick}
      className={`flex items-center justify-between ${compact ? 'p-2 md:p-3' : 'p-3 md:p-4'} rounded-xl bg-muted/30 border border-border/30 hover:border-primary/30 hover:bg-muted/50 transition-all cursor-pointer active:scale-[0.98]`}
    >
      {/* Player A */}
      <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
        <span className={`font-semibold text-foreground ${compact ? 'text-xs' : 'text-xs md:text-sm'} truncate`}>
          {truncateName(playerAName)}
        </span>
        <Avatar className={compact ? 'h-6 w-6' : 'h-7 w-7 md:h-8 md:w-8'}>
          <AvatarImage src={match.player_a?.avatar || undefined} alt={`${playerAName} avatar`} />
          <AvatarFallback className="text-[10px] bg-primary/20">{playerAName[0]}</AvatarFallback>
        </Avatar>
      </div>

      {/* Score */}
      <div className={`${compact ? 'px-2 md:px-4' : 'px-3 md:px-6'} text-center flex-shrink-0`}>
        <span className={`font-mono ${compact ? 'text-sm md:text-lg' : 'text-lg md:text-2xl'} font-bold text-primary tabular-nums`}>
          {match.score_a ?? 0} : {match.score_b ?? 0}
        </span>
        {match.match_date && (
          <div className={`${compact ? 'text-[9px]' : 'text-[10px] md:text-xs'} text-muted-foreground mt-0.5`}>
            {format(new Date(match.match_date), 'hh:mm a')}
          </div>
        )}
      </div>

      {/* Player B */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <Avatar className={compact ? 'h-6 w-6' : 'h-7 w-7 md:h-8 md:w-8'}>
          <AvatarImage src={match.player_b?.avatar || undefined} alt={`${playerBName} avatar`} />
          <AvatarFallback className="text-[10px] bg-secondary/20">{playerBName[0]}</AvatarFallback>
        </Avatar>
        <span className={`font-semibold text-foreground ${compact ? 'text-xs' : 'text-xs md:text-sm'} truncate`}>
          {truncateName(playerBName)}
        </span>
      </div>
    </div>
  );
};

function TodayMatchesTickerComponent({ matches, isLoading }: TodayMatchesTickerProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const todayDate = format(new Date(), 'MMMM dd, yyyy');
  const [isPaused, setIsPaused] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Show fewer items on mobile for performance
  const VISIBLE_COUNT = isMobile ? 3 : 5;
  const shouldAnimate = !isMobile && matches.length > VISIBLE_COUNT; // Disable auto-scroll on mobile

  // Auto-scroll effect
  useEffect(() => {
    if (!shouldAnimate || isPaused || matches.length <= VISIBLE_COUNT) return;
    
    const interval = setInterval(() => {
      setScrollPosition((prev) => {
        const next = prev + 1;
        return next >= matches.length ? 0 : next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused, matches.length, shouldAnimate]);

  // Get visible matches with wrap-around
  const getVisibleMatches = () => {
    if (matches.length <= VISIBLE_COUNT) return matches;
    
    const visible: TodayMatch[] = [];
    for (let i = 0; i < VISIBLE_COUNT; i++) {
      const index = (scrollPosition + i) % matches.length;
      visible.push(matches[index]);
    }
    return visible;
  };

  const visibleMatches = getVisibleMatches();

  if (isLoading) {
    return (
      <Card variant="glass" className="overflow-hidden">
        <CardHeader className="pb-3 p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-xl">
            <div className="p-1.5 md:p-2 rounded-lg bg-primary/10">
              <Calendar className="h-4 w-4 md:h-5 md:w-5 text-primary" />
            </div>
            Today's Matches
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-shimmer h-14 rounded-xl bg-muted/30"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card variant="glass" className="overflow-hidden">
        <CardHeader className="pb-3 p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-xl">
            <div className="p-1.5 md:p-2 rounded-lg bg-primary/10">
              <Calendar className="h-4 w-4 md:h-5 md:w-5 text-primary" />
            </div>
            Today's Matches
          </CardTitle>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">{todayDate}</p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="text-center py-8 text-muted-foreground">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/30 mb-3">
              <Calendar className="h-6 w-6 opacity-40" />
            </div>
            <p className="font-medium text-sm">No matches scheduled for today</p>
            <p className="text-xs mt-1">Check back later for updates</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="glass" className="overflow-hidden">
      <CardHeader className="pb-3 p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base md:text-xl">
              <div className="p-1.5 md:p-2 rounded-lg bg-primary/10">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-primary" />
              </div>
              Today's Matches
            </CardTitle>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">{todayDate}</p>
          </div>

          {/* View All Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="hover:bg-primary/10 hover:text-primary gap-1.5" aria-label="View all today's matches">
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">View All</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  All Today's Matches ({matches.length})
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-2 max-h-[calc(100vh-120px)] overflow-y-auto pr-2">
                {matches.map((match) => (
                  <MatchItem key={match.id} match={match} onClick={() => navigate(`/matches/${match.id}`)} />
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </CardHeader>

      <CardContent className="p-4 md:p-6 pt-0">
        {/* Ticker Container */}
        <div
          ref={containerRef}
          className="relative overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          <div className="space-y-2 transition-all duration-500 ease-in-out">
            {visibleMatches.map((match, index) => (
              <div
                key={`${match.id}-${index}`}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <MatchItem match={match} compact onClick={() => navigate(`/matches/${match.id}`)} />
              </div>
            ))}
          </div>
        </div>

        {/* Match Count Badge */}
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {Math.min(VISIBLE_COUNT, matches.length)} of {matches.length} matches
          </span>
          {shouldAnimate && (
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-amber-500' : 'bg-green-500 animate-pulse'}`} />
              {isPaused ? 'Paused' : 'Auto-scrolling'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Memoize for performance
export const TodayMatchesTicker = memo(TodayMatchesTickerComponent);
